import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import IssEditPage from "../../../../../../presentation/pages/admin/iss/edit";
import { makeRemoteIss } from "../../../../usecases/iss/remote-iss-factory";
import { makeRemoteNationalService } from "../../../../usecases/national-service/remote-national-service-factory";
import { makeRemoteAddresss } from "../../../../usecases/address-controller/remote-address-controller-factory";
import { IssModel } from "../../../../../../domain/models/iss";
import { NationalServiceModel } from "../../../../../../domain/models/national-service";
import { CityProps, StateProps } from "../../../../../../domain/models/address.model";
import { GroupProp } from "../../../../../../presentation/components/form-factory/utils/util";
import { formatNationalTaxCode } from "../../../../../../utils/tax";
import toastUtils from "../../../../../../utils/ToastUtils";

const MakeIssEdit: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [iss, setIss] = useState<IssModel>();
  // States for selectors
  const [states, setStates] = useState<StateProps[]>([]);
  const [cities, setCities] = useState<CityProps[]>([]);
  const [selectedState, setSelectedState] = useState<number>();
  const [nationalServices, setNationalServices] = useState<NationalServiceModel[]>([]);
  const [searchingNs, setSearchingNs] = useState(false);

  const issService = useMemo(() => makeRemoteIss(), []);
  const addressService = useMemo(() => makeRemoteAddresss(), []);
  const nsService = useMemo(() => makeRemoteNationalService(), []);

  // Load initial data for Edit
  useEffect(() => {
    if (id && id !== "new") {
      (async () => {
        try {
          const response = await issService.loadById(Number(id));
          setIss(response);

          if (response.cityIbgeCode) {
            const stateCode = Number(response.cityIbgeCode.substring(0, 2));
            setSelectedState(stateCode);
          }
        } catch (error) {
          toastUtils.showMessage({ type: "error", message: "Erro ao carregar dados do ISS." });
        }
      })();
    }
  }, [id, issService]);

  // Load States
  useEffect(() => {
    (async () => {
      try {
        const response = await addressService.loadStates();
        setStates(response);
      } catch (error) {
        console.error("Failed to load states", error);
      }
    })();
  }, [addressService]);

  // Load Cities when state changes
  useEffect(() => {
    if (selectedState) {
      (async () => {
        try {
          const response = await addressService.loadCities(selectedState);
          setCities(response);
        } catch (error) {
          console.error("Failed to load cities", error);
        }
      })();
    } else {
      setCities([]);
    }
  }, [selectedState, addressService]);

  const handleSubmit = useCallback(async (values: any) => {
    try {
      const payload: IssModel = {
        cityIbgeCode: values.cityIbgeCode,
        nationalService: {
          id: Number(values.nationalServiceId),
        },
        aliquot: Number(values.aliquot),
      };

      if (id && id !== "new") {
        await issService.update(Number(id), payload);
        toastUtils.showMessage({ type: "success", message: "ISS atualizado com sucesso!" });
      } else {
        await issService.create(payload);
        toastUtils.showMessage({ type: "success", message: "ISS criado com sucesso!" });
      }
      history.push("/admin/iss");
    } catch (error) {
      toastUtils.showMessage({ type: "error", message: "Erro ao salvar configuração de ISS. Verifique os dados." });
    }
  }, [id, issService, history]);

  const groups: GroupProp[] = useMemo(() => [
    {
      groupName: "LOCATION",
      groupLabel: "Localidade e Serviço",
      items: [
        {
          name: "state",
          type: "select",
          label: "Estado",
          placeholder: "Selecione o estado",
          showClear: true,
          options: states.map(s => ({ label: s.name || s.initials || "", value: s.id })),
          onChange: (val: any) => setSelectedState(val.value),
          initialValue: iss?.cityIbgeCode ? Number(iss.cityIbgeCode.substring(0, 2)) : undefined,
          col: { xs: 12, md: 4, lg: 4 }
        },
        {
          name: "cityIbgeCode",
          type: "select",
          label: "Cidade",
          placeholder: "Selecione a cidade",
          showClear: true,
          options: cities.map(c => ({ label: c.name || "", value: String(c.ibge || "") })),
          validations: "inputRequired",
          required: true,
          initialValue: iss?.cityIbgeCode,
          col: { xs: 12, md: 8, lg: 8 }
        },
        {
          name: "nationalServiceId",
          type: "select",
          label: "Serviço Nacional",
          placeholder: "Selecione o serviço",
          filter: true,
          showClear: true,
          options: nationalServices.map(ns => ({
            label: `${formatNationalTaxCode(ns.nationalTaxCode)} - ${ns.description}`,
            value: ns.id
          })),
          validations: "inputRequired",
          required: true,
          loading: searchingNs,
          initialValue: iss?.nationalService?.id,
          col: { xs: 12, md: 12, lg: 12 }
        },
      ],
    },
    {
      groupName: "TAX",
      groupLabel: "Configurações de Imposto",
      items: [
        {
          name: "aliquot",
          type: "text",
          label: "Alíquota (%)",
          placeholder: "2.00 a 5.00",
          validations: "inputRequired",
          mask: "9.99",
          required: true,
          initialValue: iss?.aliquot,
          col: { xs: 12, md: 6, lg: 6 }
        },
      ],
    }
  ], [states, cities, nationalServices, searchingNs, iss]);

  const initialData = useMemo(() => {
    if (!iss) return {};
    return {
      id: iss.id,
      state: iss.cityIbgeCode ? Number(iss.cityIbgeCode.substring(0, 2)) : undefined,
      cityIbgeCode: iss.cityIbgeCode,
      nationalServiceId: iss.nationalService?.id,
      aliquot: iss.aliquot,
    };
  }, [iss]);

  // Load all National Services on mount for the select
  useEffect(() => {
    (async () => {
      try {
        setSearchingNs(true);
        const response = await nsService.search("");
        setNationalServices(response);
      } catch (error) {
        console.error("Failed to load national services", error);
      } finally {
        setSearchingNs(false);
      }
    })();
  }, [nsService]);

  return (
    <IssEditPage
      groups={groups}
      onSubmit={handleSubmit}
      initialData={initialData}
    />
  );
};

export default MakeIssEdit;
